import { Calendar, Pencil, Store, Trash2 } from "lucide-react"
import { Avatar } from "@/components/ui/Avatar"
import { Button } from "@/components/ui/Button"
import type { PushAction } from "../data"
import { shortDate } from "../dates"
import NoteText from "./NoteText"

function dateRange(from: string | null, to: string | null) {
  if (!from) return to ? `Until ${shortDate(to)}` : null
  return !to || to === from ? shortDate(from) : `${shortDate(from)} – ${shortDate(to)}`
}

interface Props {
  action: PushAction
  onEdit: () => void
  onDelete: () => void
}

// One decided action, read-only. Edit and delete stay visible in a quiet tone.
export default function ActionRow({ action, onEdit, onDelete }: Props) {
  const dates = dateRange(action.from, action.to)
  return (
    <div className="flex items-center gap-4 px-4 py-3 text-sm">
      <span className="w-40 shrink-0 font-semibold">{action.type}</span>
      <div className="min-w-0 flex-1 whitespace-pre-line">
        {action.note ? <NoteText note={action.note} /> : <span className="text-muted-foreground">No note</span>}
      </div>
      {action.channel && (
        <span className="flex shrink-0 items-center gap-1">
          <Store className="h-4 w-4" />
          {action.channel}
        </span>
      )}
      {dates && (
        <span className="flex shrink-0 items-center gap-1 tabular-nums">
          <Calendar className="h-4 w-4" />
          {dates}
        </span>
      )}
      {action.pics.length > 0 && (
        <span className="flex shrink-0 items-center gap-1.5">
          <span className="flex -space-x-1">
            {action.pics.map((name) => <Avatar key={name} name={name} tone="primary" className="ring-2 ring-background" />)}
          </span>
          {action.pics.map((name) => name.split(" ")[0]).join(", ")}
        </span>
      )}
      <span className="flex shrink-0 items-center">
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={onEdit} aria-label="Edit action">
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={onDelete} aria-label="Delete action">
          <Trash2 className="h-4 w-4" />
        </Button>
      </span>
    </div>
  )
}
